import { HTTP_REQUEST_METHODS } from '@js/constants/js-constants.es6.js';
import ApiErrorHandler from '@js/errors/api-error-handler.es6.js';

export default class HttpClient {
  #basePath;

  constructor(basePath) {
    this.#basePath = basePath;
  }

  /**
   * Use the fetch API to request data from the API provided by Luminis Health.
   * If the patient is currently authenticated on the site, the bearer token value
   * will be included in the authorization header.
   *
   * @param {object} options options
   * @param {string} options.method the HTTP method to use with the Fetch API
   * @param {string} options.relativePath the relative path to the Luminis Health API
   * @param {object} options.body the data to JSON stringify and send with the request
   * @param {boolean} options.cache the cache option to use with the Fetch API
   * @param errorMessage An error message more specific to be thrown
   * @param errorRedirectPage A page to redirect to on failure, if provided
   * @param successRedirectPage A page to redirect on success, if provided
   * @param handleErrorElsewhere There are use cases where a page expects an error and needs to handle it specially; if so just return it
   * @return {Promise<object>} a Promise that resolves to the fetched data
   */
  async #fetch({ method, relativePath, body, headers, cache }, errorMessage, errorRedirectPage, successRedirectPage, handleErrorElsewhere) {
    let path = this.#basePath;
    const options = {
      method,
      headers
    };

    // include creds for oauth
    options.credentials = 'include';

    if (body) {
      options.body = body;
    }

    if (cache) {
      options.cache = cache;
    }

    if (relativePath) {
      // Remove leading slash from relativePath if path already ends with one
      const cleanRelativePath = relativePath.startsWith('/') && path.endsWith('/')
        ? relativePath.slice(1)
        : relativePath;
      path += cleanRelativePath;
    }

    const response = await fetch(path, options);

    let responseBody;
    try {
      responseBody = await response.json();
    } catch {
      // The response body may not be valid JSON for network errors
    }

    if (response.status === 200) {
      if (successRedirectPage) {
        window.location.href = successRedirectPage;
      }
    }
    else {
      if (!handleErrorElsewhere) {
        ApiErrorHandler.handleError(
          response.status,
          response.statusText,
          method,
          path,
          responseBody,
          errorMessage,
          errorRedirectPage
        );
      }
    }

    return responseBody;
  }

  async get({ relativePath, headers = {}, cache }, errorMessage, errorRedirectPage, successRedirectPage, handleErrorElsewhere) {
    const options = {
      method: HTTP_REQUEST_METHODS.GET,
      relativePath,
      headers,
      cache,
    };

    return this.#fetch(options, errorMessage, errorRedirectPage, successRedirectPage, handleErrorElsewhere);
  }

  async postJson({ relativePath, body = {}, headers = {}, cache }, errorMessage, errorRedirectPage, successRedirectPage, handleErrorElsewhere) {
    const options = {
      method: HTTP_REQUEST_METHODS.POST,
      relativePath,
      body: JSON.stringify(body),
      headers,
      cache,
    };

    return this.#fetch(options, errorMessage, errorRedirectPage, successRedirectPage, handleErrorElsewhere);
  }

  async postFormData({ relativePath, body = {}, headers = {}, cache }, errorMessage, errorRedirectPage, successRedirectPage, handleErrorElsewhere) {
    const options = {
      method: HTTP_REQUEST_METHODS.POST,
      relativePath,
      body: new URLSearchParams(body),
      headers,
      cache,
    };

    return this.#fetch(options, errorMessage, errorRedirectPage, successRedirectPage, handleErrorElsewhere);
  }

  async putJson({ relativePath, body = {}, headers = {}, cache }, errorMessage, errorRedirectPage, successRedirectPage, handleErrorElsewhere) {
    const options = {
      method: HTTP_REQUEST_METHODS.PUT,
      relativePath,
      body: JSON.stringify(body),
      headers,
      cache,
    };

    return this.#fetch(options, errorMessage, errorRedirectPage, successRedirectPage, handleErrorElsewhere);
  }

  async putFormData({ relativePath, body = {}, headers = {}, cache }, errorMessage, errorRedirectPage, successRedirectPage, handleErrorElsewhere) {
    const options = {
      method: HTTP_REQUEST_METHODS.PUT,
      relativePath,
      body: new URLSearchParams(body),
      headers,
      cache,
    };

    return this.#fetch(options, errorMessage, errorRedirectPage, successRedirectPage, handleErrorElsewhere);
  }

  async delete({ relativePath, headers = {}, cache }, errorMessage, errorRedirectPage, successRedirectPage, handleErrorElsewhere) {
    const options = {
      method: HTTP_REQUEST_METHODS.DELETE,
      relativePath,
      headers,
      cache,
    };

    return this.#fetch(options, errorMessage, errorRedirectPage, successRedirectPage, handleErrorElsewhere);
  }
}
